import { CONFIG, type EchoDef } from "./config";
import type { PositionHistory, Vec2 } from "./history";
import { wallBandSdf, type Wall } from "./levels";
import type { SplitMix64 } from "./splitmix64";

/**
 * Deterministic shard placement (spec §6.4).
 *
 * Exactly 3 RNG draws per candidate (x, y, jitter) regardless of rejection,
 * so the draw sequence — and therefore every later placement — is identical
 * in both engines no matter which candidates get rejected.
 */

export interface SpawnerEnv {
  rng: SplitMix64;
  history: PositionHistory;
  /**
   * Effective echo playback time `stepsAhead` mechanics steps from now —
   * stasis-aware (Worlds §W5) so route prediction matches real replay.
   */
  echoTimeAt: (stepsAhead: number) => number;
  /** current mechanics step (gates evaluate their band clearance from it) */
  stepIndex: number;
  playerX: number;
  playerY: number;
  prevShard: Vec2 | null;
  /** echoes currently visible (past unlock step) */
  activeEchoes: readonly EchoDef[];
  /** level walls; empty for classic/daily/practice (Worlds §W1) */
  walls: readonly Wall[];
}

function echoPositionAt(env: SpawnerEnv, def: EchoDef, stepsAhead: number): Vec2 | null {
  return env.history.positionAt(env.echoTimeAt(stepsAhead) - def.delay);
}

export function placeShard(env: SpawnerEnv): Vec2 {
  const C = CONFIG;
  let best: Vec2 | null = null;
  let bestScore = -Infinity;
  let fallback: Vec2 | null = null;
  let fallbackClearance = -Infinity;

  for (let n = 0; n < C.shardCandidates; n++) {
    const cx = C.shardBoundsMinX + env.rng.nextDouble() * (C.shardBoundsMaxX - C.shardBoundsMinX);
    const cy = C.shardBoundsMinY + env.rng.nextDouble() * (C.shardBoundsMaxY - C.shardBoundsMinY);
    const jitter = env.rng.nextDouble() * 12;

    const dpx = cx - env.playerX;
    const dpy = cy - env.playerY;
    const playerDistSq = dpx * dpx + dpy * dpy;
    const playerDist = Math.sqrt(playerDistSq);

    let minEchoDistSq = Infinity;
    for (const def of env.activeEchoes) {
      const p = echoPositionAt(env, def, 0);
      if (!p) continue;
      const dx = cx - p.x;
      const dy = cy - p.y;
      const dSq = dx * dx + dy * dy;
      if (dSq < minEchoDistSq) minEchoDistSq = dSq;
    }
    const minEchoDist = minEchoDistSq === Infinity ? Infinity : Math.sqrt(minEchoDistSq);

    // Wall clearance gates BOTH the fallback and the scored path — a shard
    // must never sit inside or against a wall (Worlds §W1), nor anywhere a
    // gate's travel band can reach (§W4). RNG draw count is untouched.
    let wallClear = true;
    for (const w of env.walls) {
      const clearance = w.type === "gate" ? C.shardGateClearance : C.shardWallClearance;
      if (wallBandSdf(w, cx, cy) < clearance) {
        wallClear = false;
        break;
      }
    }

    // Fallback candidate: greatest predicted echo clearance among all 24.
    if (wallClear && minEchoDist > fallbackClearance) {
      fallbackClearance = minEchoDist;
      fallback = { x: cx, y: cy };
    }

    // Rejections (spec §6.4)
    if (!wallClear) continue;
    if (playerDist < C.shardMinPlayerDist) continue;
    if (minEchoDist < C.shardMinEchoDist) continue;
    let prevDist: number = C.noPrevShardDist;
    if (env.prevShard) {
      const dsx = cx - env.prevShard.x;
      const dsy = cy - env.prevShard.y;
      prevDist = Math.sqrt(dsx * dsx + dsy * dsy);
      if (prevDist < C.shardMinPrevDist) continue;
    }
    if (routeCrossesEcho(env, cx, cy, playerDist)) continue;

    const echoTerm = minEchoDist === Infinity ? C.echoDistCap : Math.min(minEchoDist, C.echoDistCap);
    const score =
      echoTerm * 1.4 + prevDist * 0.35 - Math.abs(playerDist - 170) * 0.4 + jitter;

    if (score > bestScore) {
      bestScore = score;
      best = { x: cx, y: cy };
    }
  }

  // Never fail to place a shard (spec §6.4).
  if (best) return best;
  if (fallback) return fallback;
  return { x: 180, y: 150 };
}

/**
 * Reject if the straight player→candidate route passes within 20 units of any
 * echo head during the next 0.35 s (sampled at 60 Hz, both engines identical).
 * Delays are all ≥ 1.35 s > 0.35 s, so every future echo position needed here
 * is already recorded history — the check is fully deterministic.
 */
function routeCrossesEcho(env: SpawnerEnv, cx: number, cy: number, playerDist: number): boolean {
  const C = CONFIG;
  const steps = Math.round(C.routeCrossWindow * 60); // 21 samples of 1/60 s
  const dirScale = playerDist > 1e-9 ? 1 / playerDist : 0;
  const dirX = (cx - env.playerX) * dirScale;
  const dirY = (cy - env.playerY) * dirScale;
  const crossSq = C.routeCrossDist * C.routeCrossDist;

  for (let k = 1; k <= steps; k++) {
    const dt = k / 60;
    const travelled = Math.min(playerDist, C.touchSpeed * dt);
    const px = env.playerX + dirX * travelled;
    const py = env.playerY + dirY * travelled;
    for (const def of env.activeEchoes) {
      const p = echoPositionAt(env, def, k * 2); // 60 Hz sample = 2 mechanics steps
      if (!p) continue;
      const dx = px - p.x;
      const dy = py - p.y;
      if (dx * dx + dy * dy < crossSq) return true;
    }
  }
  return false;
}
